import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BILLING } from '@/constants/testIds';
import { Check, Sparkles, ShieldCheck } from 'lucide-react';

/**
 * Billing — Zita Pro pricing surface.
 *
 * Aug 2026 doctrine consolidation: Ari Search+ has been RETIRED as a
 * separate paid tier. Every capability formerly gated behind the $9.99
 * add-on is now included in Zita Pro at $29.99 / month. The addons array
 * from the API is honored (currently empty) for future extensibility, but
 * no Ari Search+ card is rendered.
 */
const Billing = () => {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/billing/plans').then(({ data }) => {
      setPlans(data.plans);
    });
  }, []);

  useEffect(() => {
    if (location.search.includes('session_id=')) {
      const id = new URLSearchParams(location.search).get('session_id');
      const check = async () => {
        try {
          const { data } = await api.get(`/billing/status/${id}`);
          if (data.payment_status === 'paid') {
            await refresh();
            toast.success('Subscription active. Welcome to Zita Pro.');
            navigate('/');
          }
        } catch { /* ignore */ }
      };
      const t = setInterval(check, 2000);
      check();
      return () => clearInterval(t);
    }
  }, [location.search, location.pathname, refresh, navigate]);

  // Legacy deep-link ?addon=collections now redirects to /subscribe.
  useEffect(() => {
    if (new URLSearchParams(location.search).get('addon')) {
      navigate('/subscribe', { replace: true });
    }
  }, [location.search, navigate]);

  const start = () => {
    navigate('/subscribe');
    void setBusy;
  };

  const portal = () => {
    navigate('/settings');
  };

  return (
    <div className="space-y-8">
      <header className="text-center max-w-2xl mx-auto">
        <div className="text-xs uppercase tracking-[0.18em] text-zita-mocha">Membership</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-zita-espresso mt-2">
          One membership. Every marketplace.
        </h1>
        <p className="mt-3 text-zita-mocha">
          Zita Pro · $29.99 / month · everything included · cancel anytime.
        </p>
      </header>

      {user?.is_admin && (
        <div className="zita-card p-5 max-w-2xl mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-ari flex items-center justify-center text-white">
            <Sparkles size={18} />
          </div>
          <div>
            <div className="font-semibold text-zita-espresso">Owner access active</div>
            <div className="text-sm text-zita-mocha">You have permanent unlimited access. No Square charges apply.</div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-1 gap-4 max-w-2xl mx-auto">
        {plans.map((p) => (
          <div
            key={p.lookup_key}
            data-testid={BILLING.monthlyCard}
            className="zita-card p-6 flex flex-col"
          >
            <div className="flex items-baseline justify-between">
              <div>
                <div data-testid={BILLING.planName} className="font-serif text-2xl text-zita-espresso">{p.name}</div>
                <div className="text-xs zita-ink-muted mt-1">The complete Smart Scan product.</div>
              </div>
              <div className="text-right">
                <div className="font-serif text-4xl text-zita-cocoa">${p.price}</div>
                <div className="text-xs text-zita-muted">/{p.interval}</div>
              </div>
            </div>
            <ul className="mt-6 space-y-2 text-sm text-zita-mocha flex-1">
              {['Every supported public source and search engine — no artificial tier gates',
                'Ari — natural-language chat, voice, and TTS with tool execution',
                'Persistent search intelligence · Attention Reduction · Trust Intelligence',
                'Deep enrichment, cross-scan comparison, review-state memory',
                'Unlimited folders · Save All Matches · Excel / CSV / PDF / Google Sheets exports',
                'Scheduled monitoring with delta detection and honest source status',
                'Mobile + desktop · no ads · cancel anytime'].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check size={16} className="mt-0.5 text-zita-cocoa shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={start}
              disabled={
                !!busy ||
                user?.is_admin ||
                user?.has_active_access
              }
              data-testid={BILLING.startTrialButton}
              className="zita-btn-primary mt-6"
            >
              {user?.is_admin || user?.has_active_access
                ? 'Access active'
                : `Subscribe — $${p.price}/${p.interval}`}
            </button>
          </div>
        ))}
      </div>

      {user?.has_billing_account && (
        <div className="text-center">
          <button
            type="button"
            onClick={portal}
            data-testid={BILLING.portalButton}
            className="zita-btn-secondary text-sm inline-flex items-center gap-2"
          >
            <ShieldCheck size={14} /> Manage billing
          </button>
        </div>
      )}

      <div className="text-xs text-zita-muted text-center max-w-lg mx-auto">
        Payments powered by Square. Automatic monthly billing enabled by default. Cancel anytime in Settings → Subscription. Every Zita Pro capability is included; there are no separate add-ons.
      </div>
    </div>
  );
};

export default Billing;
